import {
  HttpError,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { API_BASE, apiRequest, tryRefreshSession } from '@/lib/api-client';
import { accessTokenExpiringWithin, getAccessToken } from '@/lib/session';
import { REALTIME_TARGET, RealtimeMessage } from './types';

type MessageListener = (msg: RealtimeMessage) => void;
type StateListener = () => void;
type GroupStatus = 'pending' | 'subscribed' | 'failed';

const MAX_INITIAL_RETRIES = 5;
/** Coalesce bursts of state changes into a single notification. */
const STATE_NOTIFY_DEBOUNCE_MS = 50;

function log(...args: unknown[]) {
  // Only emit diagnostics during local development. Production/field builds stay
  // silent so the browser console isn't polluted while inside the panel room. (Task 15)
  if (import.meta.env.DEV) {
    console.info('[realtime]', ...args);
  }
}

/**
 * Singleton wrapper around the Azure SignalR connection (serverless mode).
 *
 * Lifecycle:
 *   - The standard @microsoft/signalr negotiation POSTs to `${API_BASE}/api/realtime/negotiate`
 *     with the user's bearer token; the API returns the Azure { url, accessToken }.
 *   - Group membership is server-authorized via /api/realtime/subscribe, so we
 *     reference-count groups here and (un)subscribe over REST as pages mount.
 *
 * Best-effort: if real-time is not configured (negotiate 503) we stop trying and
 * pages keep working through their polling fallback. Transient failures are
 * retried with backoff, because `withAutomaticReconnect` only covers drops
 * *after* a successful initial connect — not a failed first attempt.
 */
export class RealtimeConnection {
  private connection: HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  private readonly listeners = new Set<MessageListener>();
  /** group -> active subscriber count (so shared groups aren't dropped early). */
  private readonly groupRefs = new Map<string, number>();
  /** Set only when negotiate returns 503: real-time is intentionally off. */
  private notConfigured = false;
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Per-group subscription health, used to compute `isLive`. */
  private readonly groupStatus = new Map<string, GroupStatus>();
  /** Consumers (React hooks) notified whenever connection/group state changes. */
  private readonly stateListeners = new Set<StateListener>();
  private notifyTimer: ReturnType<typeof setTimeout> | null = null;

  get state(): HubConnectionState | 'disabled' {
    if (this.notConfigured) return 'disabled';
    return this.connection?.state ?? HubConnectionState.Disconnected;
  }

  /**
   * True when push is actually working for `group`: the socket is connected AND
   * this group's subscribe succeeded. Pages use this to choose a slow (live) vs.
   * fast (fallback) poll interval.
   */
  isLive(group: string): boolean {
    if (this.notConfigured) return false;
    return (
      this.connection?.state === HubConnectionState.Connected &&
      this.groupStatus.get(group) === 'subscribed'
    );
  }

  /** Subscribe to connection/group-state changes; returns an unsubscribe fn. */
  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  private notifyStateChange(): void {
    if (this.notifyTimer) return;
    this.notifyTimer = setTimeout(() => {
      this.notifyTimer = null;
      for (const listener of this.stateListeners) listener();
    }, STATE_NOTIFY_DEBOUNCE_MS);
  }

  /** Build + start the connection. Idempotent; retries transient failures. */
  async start(): Promise<void> {
    if (this.notConfigured) return;
    if (this.connection?.state === HubConnectionState.Connected) return;
    if (this.startPromise) return this.startPromise;

    // We're attempting now, so cancel any pending scheduled retry.
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (!getAccessToken()) {
      // No bearer token yet — defer; the provider/page will call start() again.
      log('skip start: no access token yet');
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${API_BASE}/api/realtime`, {
        // Called on the initial negotiate and on every reconnect. The app access
        // token is short-lived (~15m) and the SDK's negotiate bypasses the
        // apiRequest 401→refresh path, so refresh proactively here — otherwise a
        // reconnect after the token expires would fail with 401 and stay dead.
        accessTokenFactory: async () => {
          if (accessTokenExpiringWithin(60)) {
            await tryRefreshSession();
          }
          return getAccessToken() ?? '';
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(REALTIME_TARGET, (msg: RealtimeMessage) => {
      for (const listener of this.listeners) listener(msg);
    });
    connection.onreconnecting((err) => {
      log('reconnecting…', err?.message ?? '');
      this.notifyStateChange();
    });
    connection.onreconnected(() => {
      log('reconnected; resubscribing', [...this.groupRefs.keys()]);
      void this.resubscribeAll();
      this.notifyStateChange();
    });
    connection.onclose((err) => {
      log('connection closed', err?.message ?? '');
      this.notifyStateChange();
    });

    this.connection = connection;
    this.startPromise = connection
      .start()
      .then(() => {
        this.retries = 0;
        log('connected', `${API_BASE}/api/realtime`);
        // Re-assert any groups requested before the socket was ready.
        void this.resubscribeAll();
        this.notifyStateChange();
      })
      .catch((err: unknown) => {
        this.connection = null;
        if (err instanceof HttpError && err.statusCode === 503) {
          this.notConfigured = true;
          log('real-time not configured on API (503) — using polling fallback');
          this.notifyStateChange();
          return;
        }
        log('connect failed:', (err as Error)?.message ?? err);
        this.notifyStateChange();
        this.scheduleRetry();
      })
      .finally(() => {
        this.startPromise = null;
      });

    return this.startPromise;
  }

  private scheduleRetry(): void {
    if (this.retryTimer || this.retries >= MAX_INITIAL_RETRIES) return;
    const delay = Math.min(30_000, 2_000 * 2 ** this.retries);
    this.retries += 1;
    log(`retrying connect in ${delay}ms (attempt ${this.retries}/${MAX_INITIAL_RETRIES})`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.start();
    }, delay);
  }

  async stop(): Promise<void> {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.listeners.clear();
    this.groupRefs.clear();
    this.groupStatus.clear();
    this.retries = 0;
    this.notConfigured = false;
    const conn = this.connection;
    this.connection = null;
    if (conn) await conn.stop().catch(() => undefined);
    this.notifyStateChange();
  }

  onMessage(listener: MessageListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Join a server-authorized group; ref-counted so multiple pages can share it. */
  async subscribe(group: string): Promise<void> {
    if (this.notConfigured) return;
    const next = (this.groupRefs.get(group) ?? 0) + 1;
    this.groupRefs.set(group, next);
    if (next === 1) {
      this.groupStatus.set(group, 'pending');
      this.notifyStateChange();
      await this.sendSubscribe(group);
    }
  }

  async unsubscribe(group: string): Promise<void> {
    const current = this.groupRefs.get(group) ?? 0;
    if (current <= 1) {
      this.groupRefs.delete(group);
      this.groupStatus.delete(group);
      this.notifyStateChange();
      if (current === 1) await this.sendUnsubscribe(group);
    } else {
      this.groupRefs.set(group, current - 1);
    }
  }

  private async resubscribeAll(): Promise<void> {
    await Promise.all([...this.groupRefs.keys()].map((group) => this.sendSubscribe(group)));
  }

  private async sendSubscribe(group: string): Promise<void> {
    try {
      await apiRequest('/api/realtime/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group }),
      });
      // Only mark live if the group is still wanted (not unsubscribed meanwhile).
      if (this.groupRefs.has(group)) this.groupStatus.set(group, 'subscribed');
      log('subscribed', group);
    } catch (err) {
      if (this.groupRefs.has(group)) this.groupStatus.set(group, 'failed');
      log('subscribe failed', group, (err as Error)?.message ?? err);
    } finally {
      this.notifyStateChange();
    }
  }

  private async sendUnsubscribe(group: string): Promise<void> {
    try {
      await apiRequest('/api/realtime/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group }),
      });
    } catch {
      // ignore — leaving a group is best-effort
    }
  }
}

export const realtime = new RealtimeConnection();
